import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingSection = () => {
  const navigate = useNavigate();

  const tiers = [
    {
      name: 'Starter',
      price: '€9',
      period: '/user/mo',
      description: 'Perfect for small teams getting started',
      features: [
        'Basic onboarding workflows',
        'AI search (10 queries/day)',
        '10 integrations',
        'Email support',
        '5GB storage'
      ],
      cta: 'Start Free Trial',
      highlighted: false
    },
    {
      name: 'Growth',
      price: '€19',
      period: '/user/mo',
      badge: 'Most Popular',
      description: 'For growing teams scaling onboarding',
      features: [
        'Full workflow automation',
        'Unlimited AI Buddy queries',
        '30 integrations',
        'Analytics & insights',
        'Badge gamification system',
        'Priority support',
        '50GB storage'
      ],
      cta: 'Start Free Trial',
      highlighted: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For large organizations with advanced needs',
      features: [
        'SSO (Azure/Okta/Google)',
        'SLA 99.99% uptime',
        'Private cloud/on-prem',
        'Dedicated CSM',
        'Custom workflows & automations',
        'Unlimited integrations & admins',
        'Unlimited storage',
        'White-label options'
      ],
      cta: 'Contact Sales',
      highlighted: false
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-[#0F0F11] to-[#0B1220]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#FFD100]/10 border border-[#FFD100]/20 rounded-full px-4 py-2 mb-6">
            <Icon name="DollarSign" size={16} color="#FFD100" />
            <span className="text-[#FFD100] text-sm font-semibold">TRANSPARENT PRICING</span>
          </div>
          <h2 className="text-5xl font-bold text-white mb-6">
            Plans That Scale<br />
            <span className="text-[#FFD100]">With Your Growth</span>
          </h2>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Start free, upgrade when you need more. No hidden fees.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {tiers?.map((tier, index) => (
            <div
              key={index}
              className={`relative backdrop-blur-xl rounded-3xl p-8 transition-all duration-500 ${
                tier?.highlighted
                  ? 'bg-white/10 border-2 border-[#FFD100] shadow-2xl shadow-[#FFD100]/20 scale-105'
                  : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20'
              }`}>
              {tier?.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-[#FFD100] text-[#0B1220] px-4 py-2 rounded-full text-sm font-bold">
                    {tier?.badge}
                  </div>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">{tier?.name}</h3>
                <p className="text-white/60 text-sm mb-6">{tier?.description}</p>
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="text-5xl font-bold text-white">{tier?.price}</span>
                  {tier?.period && <span className="text-white/60">{tier?.period}</span>}
                </div>
              </div>

              <div className="space-y-4 mb-8">
                {tier?.features?.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#FFD100]/20 rounded-full flex items-center justify-center mt-0.5">
                      <Icon name="Check" size={12} color="#FFD100" strokeWidth={3} />
                    </div>
                    <span className="text-white/80 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <Button
                variant={tier?.highlighted ? 'default' : 'outline'}
                size="lg"
                onClick={() => navigate('/login-authentication')}
                className={`w-full ${
                  tier?.highlighted
                    ? 'bg-[#FFD100] hover:bg-[#FFD100]/90 text-[#0B1220] font-bold'
                    : 'border-2 border-white/20 text-white hover:bg-white/10'
                }`}>
                {tier?.cta}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-white/60 text-sm">
            All plans include 14-day free trial • No credit card required • Cancel anytime
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;